import { startsWith, endsWith } from 'lodash';
import { getLocation } from '@sda/utils/location';
import { EMBED_CONFIG, getStreamingProfileDetails } from '@sda/utils/url';
import type { Location } from '@sda/utils/location/interfaces';
import type { Act } from '@sda/interfaces/act';
import type {
  BandcampConfiguration, BandcampResponse,
  SoundcloudConfiguration, SoundcloudResponse, SpotifyConfiguration, SpotifyResponse,
  VimeoConfiguration, VimeoResponse, YoutubeConfiguration, YoutubeResponse,
} from '@sda/utils/url/interfaces';
import type { GetLocationDataDto, GetLocationDataResponse } from '@sda/functions/system/get-location-data.endpoint';
import type { FunctionsResponse, Methods, OptionalResponseParameters } from '@sda/functions';
import type { GetVenueLocationDataResponse } from '@sda/functions/system/get-venue-location-data.endpoint';

class HttpError extends Error {
  constructor(message: string, public code: number) {
    super(message);

    this.code = code;
    this.name = 'HttpError';
  }
}

/* Firebase Functions Requests */

/**
 * Construct a fetch request with the given parameters
 * @param  {string} url
 * @param  {string} method
 * @param  {any} body?
 * @param  {any} headers?
 * @return {Promise<any>}
 */
export const buildRequest = <T>({
  url, method, body, headers,
}: { url: string, method: Methods, body?: T, headers?: HeadersInit }): Promise<Response> => {
  let requestUrl = `${process.env.FIREBASE_CLOUD_FUNCS_BASE_URL}${url}`;

  if (startsWith(url, 'http')) {
    requestUrl = url;
  }

  return fetch(requestUrl, {
    method,
    headers: new Headers({
      'Content-Type': 'application/json',
      Accept: 'application/json',
      ...headers || {},
    }),
    body: (body ? JSON.stringify(body) : undefined),
  });
};

/**
 * Build an authenticated Firebase Functions HTTPS request
 * @param  {Firebase} firebase
 * @param  {string} url
 * @param  {string} method
 * @param  {any} body?
 * @return {Promise<any>}
 */
export const firebaseRequest = async <T extends object>({
  url, method = 'GET', body = undefined,
}: { url: string, method: Methods, body?: T }) => {
  const firebase = window.Firebase;

  if (!firebase || endsWith(url, 'system/token')) {
    return buildRequest({
      url, method, body, headers: { Authorization: '' },
    });
  }

  let request: Promise<Response>;

  try {
    const idToken = await firebase.auth.currentUser.getIdToken();

    request = buildRequest({
      url, method, body, headers: { Authorization: `Bearer ${idToken}` },
    });
  } catch (e) {
    request = buildRequest({
      url, method, body, headers: { Authorization: '' },
    });
  }

  return request;
};

/**
 * Run an HTTP fetch request
 * @param  {Promise<any>} request
 * @return {Promise<any>}
 */
export const handleRequest = async <T>(response: Response): Promise<FunctionsResponse<T & OptionalResponseParameters> | never> => {
  if (response.status === 401) {
    throw new HttpError(response.statusText, 401);
  } else if (response.status >= 400) {
    const json = await response.json() as FunctionsResponse<T & OptionalResponseParameters>;

    if (json?.error) {
      throw new HttpError(json.error.message, response.status);
    }

    throw new HttpError(json as any, response.status);
  } else {
    const json = await response.json() as FunctionsResponse<T & OptionalResponseParameters>;

    if (json && json.data && json.data.redirectUrl) {
      window.location.href = json.data.redirectUrl;
    }

    return json;
  }
};

/**
 * Get the value for a given key in address_components
 * From https://stackoverflow.com/a/40144130/2529520
 *
 * @param {Array} components address_components returned from Google maps autocomplete
 * @param type key for desired address component
 * @returns {String} value, if found, for given type (key)
 */
const extractFromAddress = (components: google.maps.GeocoderAddressComponent[], type: string, key = 'long_name'): string => components.filter(
  (component) => component.types.indexOf(type) !== -1,
).map((item) => item[key]).pop() || null;

const lookupPlaceDetails = (placeId: string): Promise<google.maps.places.PlaceResult> => new Promise((resolve) => {
  const service = new window.google.maps.places.PlacesService(document.createElement('div'));

  service.getDetails({ placeId }, resolve);
});

const lookupNearbyDetails = (request: google.maps.places.PlaceSearchRequest): Promise<google.maps.places.PlaceResult[]> => new Promise((resolve) => {
  const service = new window.google.maps.places.PlacesService(document.createElement('div'));

  service.nearbySearch(request, resolve);
});

const extractPlaceDataFromResult = (result: google.maps.places.PlaceResult) => {
  const {
    address_components: components, geometry, types, name,
  } = result;

  const streetNumber = extractFromAddress(components, 'street_number', 'short_name');
  const route = extractFromAddress(components, 'route', 'short_name');

  const postalCode = extractFromAddress(components, 'postal_code');

  const regionCode = extractFromAddress(components, 'administrative_area_level_1', 'short_name');
  const countryCode = extractFromAddress(components, 'country', 'short_name');

  let city = extractFromAddress(components, 'locality');

  if (!city) {
    city = extractFromAddress(components, 'administrative_area_level_3');
  }

  if (!city) {
    city = extractFromAddress(components, 'postal_town');
  }

  if (!city) {
    city = extractFromAddress(components, 'sublocality');
  }

  return {
    streetNumber,
    route,
    city,
    regionCode,
    countryCode,
    postalCode,
    types,
    name,
    latLng: {
      lat: geometry.location.lat(),
      lng: geometry.location.lng(),
    },
  };
};

export const getPlaceDetails = async (placeId: string) => {
  const result = await lookupPlaceDetails(placeId);

  return extractPlaceDataFromResult(result);
};

export const getNearbyPlaces = async (location: Location) => {
  const [result] = await lookupNearbyDetails({ location, radius: 50000 });
  const details = await lookupPlaceDetails(result.place_id);

  return extractPlaceDataFromResult(details);
};

interface LocationResponse {
  readonly compiledAddress: string;
  readonly taxRate: number;
  readonly timezoneOverride: string;
  readonly civicAddress: string;
  readonly city: string;
  readonly region: string;
  readonly country: string;
  readonly latLng: FirebaseFirestore.GeoPoint;
  readonly fuzzyLatLng: FirebaseFirestore.GeoPoint;
  readonly postalCode: string;
}

export const getNormalizedGeocodeResult = async (location: Location, civicAddress: string) => {
  const request = await firebaseRequest<GetLocationDataDto>({
    url: `${process.env.API_BASE_URL}/system/getLocationData`,
    method: 'POST',
    body: {
      civicAddress,
      latLng: location,
    },
  });

  const response = await handleRequest<GetLocationDataResponse>(request);

  if (response.status === true) {
    return response.data;
  }

  throw new Error('There was a problem with your address. Please ensure you have entered the full street address of your space and try again.');
};

export const getLocationData = async (googlePlaceId: string): Promise<LocationResponse> => {
  const firebase = window.Firebase;

  const details = await getPlaceDetails(googlePlaceId);

  let civicAddress = `${details.streetNumber || ''} ${details.route || ''}`;
  let compiledAddress = `${civicAddress} ${details.city || ''} ${details.regionCode || ''} ${details.countryCode || ''}`;

  if (!details.streetNumber) {
    if (details.types.includes('point_of_interest')) {
      civicAddress = details.name;
      compiledAddress = `${civicAddress} ${details.city || ''} ${details.regionCode || ''} ${details.countryCode || ''}`;
    } else {
      throw new Error('There was a problem with your address. Please ensure you have entered the full street address of the space and try again.');
    }
  }

  const response = await getNormalizedGeocodeResult(details.latLng, civicAddress);

  const {
    timezone, fuzzyLatLng, taxRate, postalCode,
  } = response;

  if (!fuzzyLatLng) {
    throw new Error('There was a problem with your address. Please ensure you have entered the full street address of the space and try again.');
  }

  const location = getLocation(fuzzyLatLng);

  return {
    civicAddress,
    compiledAddress,
    taxRate,
    timezoneOverride: timezone,
    city: details.city,
    region: details.regionCode,
    country: details.countryCode,
    latLng: firebase.helpers.geopoint(details.latLng.lat, details.latLng.lng),
    fuzzyLatLng: firebase.helpers.geopoint(location.lat, location.lng),
    postalCode: postalCode || details.postalCode,
  };
};

export const getLocationDetails = async (act: Pick<Partial<Act>, 'googlePlaceId' | 'latLng' | 'compiledAddress'>) => {
  const firebase = window.Firebase;

  if (act.latLng && act.googlePlaceId && act.compiledAddress) {
    let details = await getPlaceDetails(act.googlePlaceId);

    if (!details.regionCode && !details.countryCode) {
      details = await getNearbyPlaces(details.latLng);
    }

    let compiledAddress = details.city;

    if (details.regionCode) {
      compiledAddress = `${compiledAddress}, ${details.regionCode}`;
    } else if (details.countryCode) {
      compiledAddress = `${compiledAddress}, ${details.countryCode}`;
    }

    return {
      compiledAddress,
      city: details.city,
      region: details.regionCode || '',
      country: details.countryCode || '',
      latLng: firebase.helpers.geopoint(details.latLng.lat, details.latLng.lng),
    };
  }

  return null;
};

export async function checkEmbedUri(
  data: BandcampResponse | unknown,
  config: BandcampConfiguration,
): Promise<BandcampResponse>;
export async function checkEmbedUri(
  data: SoundcloudResponse | unknown,
  config: SoundcloudConfiguration,
): Promise<SoundcloudResponse>;
export async function checkEmbedUri(
  data: SpotifyResponse | unknown,
  config: SpotifyConfiguration,
): Promise<SpotifyResponse>;
export async function checkEmbedUri(
  data: VimeoResponse | unknown,
  config: VimeoConfiguration,
): Promise<VimeoResponse>;
export async function checkEmbedUri(
  data: YoutubeResponse | unknown,
  config: YoutubeConfiguration,
): Promise<YoutubeResponse>;
export async function checkEmbedUri(
  data: BandcampResponse | SoundcloudResponse | SpotifyResponse | VimeoResponse | YoutubeResponse | unknown,
  config: BandcampConfiguration | SoundcloudConfiguration | SpotifyConfiguration | VimeoConfiguration | YoutubeConfiguration,
): Promise<BandcampResponse | SoundcloudResponse | SpotifyResponse | VimeoResponse | YoutubeResponse> {
  const {
    KEY, METAKEY, PROVIDER_NAME,
  } = config;

  const uri = data[KEY];
  const metadata = data[METAKEY];

  if (!uri) {
    return {} as any;
  }

  const embedData = getStreamingProfileDetails(uri);

  if (!embedData.artistId) {
    throw new Error(`There was a problem with your ${PROVIDER_NAME} Url. Please make sure it is correct and try again.`);
  }

  if (metadata && uri === metadata.sourceUri) {
    return {} as any;
  }

  if (embedData.oEmbed === true || embedData.generateBandcampEmbedUrl === true) {
    const request = await firebaseRequest({
      url: `${process.env.API_BASE_URL}/system/${PROVIDER_NAME}?uri=${embedData.uri}`,
      method: 'GET',
    });

    const response = await handleRequest(request);

    if (response.status === false) {
      throw new Error(response.error.message);
    }

    return {
      [KEY]: uri,
      [METAKEY]: {
        embedUri: response.data,
        sourceUri: embedData.uri,
      },
    };
  }

  if (embedData.service === 'bandcamp') {
    return {
      [KEY]: embedData.uri,
      [METAKEY]: {
        embedUri: embedData.uri,
        sourceUri: embedData.uri,
      },
    };
  }

  return {
    [KEY]: uri,
    [METAKEY]: {
      embedUri: uri,
      sourceUri: uri,
    },
  };
}

export const getEmbedsFromData = async (data: unknown) => ({
  ...await checkEmbedUri(data, EMBED_CONFIG.BANDCAMP),
  ...await checkEmbedUri(data, EMBED_CONFIG.SOUNDCLOUD),
  ...await checkEmbedUri(data, EMBED_CONFIG.SPOTIFY),
  ...await checkEmbedUri(data, EMBED_CONFIG.VIMEO),
  ...await checkEmbedUri(data, EMBED_CONFIG.YOUTUBE),
});

export const getVenueLocation = async (venueId: string): Promise<GetVenueLocationDataResponse> => {
  if (!venueId) {
    return null;
  }

  const firebase = window.Firebase;

  const request = await firebaseRequest({
    url: `${process.env.API_BASE_URL}/system/getVenueLocationData/${venueId}`,
    method: 'GET',
  });

  const response = await handleRequest<GetVenueLocationDataResponse>(request);

  if (response.status === true) {
    const venue = response.data;

    if (!venue || !venue.civicAddress || !venue.fuzzyLatLng || !venue.latLng) {
      return null;
    }

    const { lat: fuzzyLat, lng: fuzzyLng } = getLocation(venue.fuzzyLatLng);
    const { lat, lng } = getLocation(venue.latLng);

    return {
      fuzzyLatLng: firebase.SDK.Venues.fsh.geopoint(fuzzyLat, fuzzyLng),
      city: venue.city,
      region: venue.region,
      country: venue.country,
      civicAddress: venue.civicAddress,
      compiledAddress: venue.compiledAddress,
      latLng: firebase.SDK.Venues.fsh.geopoint(lat, lng),
      postalCode: venue.postalCode,
      googlePlaceId: venue.googlePlaceId,
      taxRate: venue.taxRate,
      timezoneOverride: venue.timezoneOverride,
      currencyOverride: venue.currencyOverride,
      hasLocationOverride: venue.hasLocationOverride,
    };
  }

  return null;
};
